import {CmsContent} from "../../Model/CmsContent/CmsContent";
import React from "react";
import HeaderFooterWrapper from "../../Components/Navigation/HeaderFooterWrapper";
import {
    BulletStyle, Button,
    HighlightedText,
    HighlightedTitle,
    Section,
    List,
    ListItem,
    ListItemText,
    Text,
    Title, BannerTitle, BannerButton, Banner, Circle
} from "./RafPageComponents";
import {useNavigate, useParams} from "react-router-dom";
import {SALES_PORTAL_ROUTES} from "../../Routes/SalesPortalRoutes";
import {ApplicationContext} from "../../ApplicationContext";
import {validateRafCode} from "../../Api/Sales/RafApi";
import {isRequestError} from "../../Model/RequestError";
import {RafData, sessionStorageRafKey} from "./RafPage";
import raf_lady_1 from "../../assets/raf/raf_lady_1.png";
import raf_lady_2 from "../../assets/raf/raf_lady_2.png";


interface SuperdrugRafPageProps {
    /**
     * Array of all content currently stored in the CMS that has been made available to the Sales Portal.
     */
    readonly cmsContent: CmsContent[]
}

const SuperdrugRafPage = (props: SuperdrugRafPageProps): JSX.Element => {

    const applicationContext = React.useContext(ApplicationContext)
    const navigate = useNavigate()
    const {referralCode = ""} = useParams<keyof {readonly referralCode: string}>()
    const isEightCharacters = (str: string): boolean => str.length === 8;

    const setupComponent = async () => {
        if(!isEightCharacters(referralCode)) {
            navigate(SALES_PORTAL_ROUTES.RafError(applicationContext.urlContext))
            return
        }

        const maybeValidRaf = await validateRafCode(
            applicationContext.reseller,
            referralCode,
            applicationContext.salesPortalApiDomain
            )

        if (isRequestError(maybeValidRaf)){
            navigate(SALES_PORTAL_ROUTES.RafError(applicationContext.urlContext))
            return
        }

        sessionStorage.setItem(
            sessionStorageRafKey,
            JSON.stringify({referralCode: maybeValidRaf.code, referralCustomerRef: maybeValidRaf.generatedBy} as RafData)
        )

    }

    React.useEffect(() => {
        setupComponent()
    }, [])

    return (
        <HeaderFooterWrapper cmsContent={props.cmsContent}>
            <React.Fragment>
                <Banner>
                    <div className="container m-md-auto">
                        <div className="row h-100 w-100" style={{marginTop: "10px", marginBottom: "10px"}}>
                            <div className="col-8" style={{display: "flex"}}>
                                <div className="row" style={{alignItems: "center", minWidth: "-webkit-fill-available"}}>
                                    <BannerTitle>{"You've been invited to\njoin Superdrug Mobile\nand save 20%!"}</BannerTitle>
                                    <BannerButton href={SALES_PORTAL_ROUTES.SimOnly.Deals(applicationContext.urlContext)}>
                                        Unlock savings now
                                    </BannerButton>
                                </div>
                            </div>
                            <div className="col p-0 d-flex align-items-end" style={{position: "relative"}}>
                                <img src={raf_lady_1} style={{zIndex: "2"}} alt={"raf_lady_1"}/>
                                <Circle>
                                    {"Save\nUp to\n£12"}
                                </Circle>
                            </div>
                            <div className="col p-0 d-flex align-items-end">
                                <img src={raf_lady_2} style={{zIndex: "2"}} alt={"raf_lady_2"}/>
                            </div>
                        </div>
                    </div>
                </Banner>

                <div className={"mw-md ms-auto me-auto p-4"}>
                    <Section>
                        <HighlightedTitle>Get mates rates today!</HighlightedTitle>
                        <Text>
                            {"You have a great friend; they have shared an exclusive deal with you\n" +
                                "so you can make BIG savings on your mobile with us."}
                        </Text>
                        <Text>
                            {"Join us today and "}<strong>enjoy up to £12* in savings</strong>{" from your first\n" +
                            "renewal."}
                        </Text>
                        <Text>
                            {"That’s right, get "}<strong>20% off any of our plans for 3
                            months</strong>{". And we’ll\n" +
                            "thank your buddy for you by giving them rewards too – everybody\nwins!"}
                        </Text>
                    </Section>
                    <Section>
                        <Title>
                            How it works
                        </Title>
                        <List>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    <strong>Pick your perfect SIM only plan</strong>{" by clicking "}
                                    <HighlightedText>Join now</HighlightedText>
                                </ListItemText>
                            </ListItem>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    <strong>Register and active your SIM</strong>
                                </ListItemText>
                            </ListItem>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    <strong>Enjoy your
                                        plan.</strong>{" From your first renewal, you’ll automatically\n" +
                                    "receive a 20% discount, which will continue for two more months."}
                                </ListItemText>
                            </ListItem>
                        </List>
                        <Text>
                            {"This exclusive offer is available only through this special referral link,\n" +
                                "so don’t miss out!"}
                        </Text>
                    </Section>
                    <Section>
                        <Title>
                            Why choose us?
                        </Title>
                        <List>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    Totally flexible, contract-free SIMs.
                                </ListItemText>
                            </ListItem>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    Keep your number and switch hassle-free.
                                </ListItemText>
                            </ListItem>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    Enjoy <strong>12GB of European roaming at no extra cost.</strong>
                                </ListItemText>
                            </ListItem>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    {"It’s like half price twice – Get 50% of your first 2 months SIM\n" +
                                        "cost back in points on your Health & Beautycard*"}
                                </ListItemText>
                            </ListItem>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    {"Get double points on all Superdrug purchases (online and in-\nstore)."}
                                </ListItemText>
                            </ListItem>
                            <ListItem>
                                <BulletStyle>✅</BulletStyle>
                                <ListItemText>
                                    Access exclusive Superdrug VIP offers and discounts.
                                </ListItemText>
                            </ListItem>
                        </List>
                        <Text>
                            <HighlightedText>Join now</HighlightedText>{" and start saving today!"}
                        </Text>
                    </Section>
                    <Section>
                        <Button href={SALES_PORTAL_ROUTES.SimOnly.Deals(applicationContext.urlContext)}>Join now</Button>
                    </Section>
                </div>
            </React.Fragment>
        </HeaderFooterWrapper>
    )
}

export default SuperdrugRafPage